// The New Message composer's editing (#596, #597): the formatting toolbar,
// the message templates and their words. Adapters over core/src/compose.rs,
// which argues each rule and which both phones call too, so a button pressed
// on any of the three writes the same characters.
//
// The message is Markdown text, as a lesson note is, rendered by the one
// renderer every note goes through (src/domain/markdown.js) — in the email
// and in the composer's Preview. Nothing here makes HTML.
import { coreSync } from './core.js';
import { documentFromRows } from './documents.js';

const str = (v) => (v == null ? '' : String(v));

const refusalOf = (r) => (r ? { code: r.code, message: r.message } : null);

/** A word of the composer's editing, by code. */
export const composeWord = (code) => coreSync().composeMessage(String(code)) ?? String(code);

/** Every code `composeWord` answers. */
export const composeWordCodes = () => [...coreSync().composeMessageCodes()];

/** The toolbar's buttons, in order: `{ format, glyph, label }`. */
export function composeFormats() {
  const core = coreSync();
  return [...core.composeFormats()].map((format) => ({
    format,
    glyph: core.composeFormatGlyph(format) ?? format,
    label: composeWord(`format_${format}`),
  }));
}

/**
 * A toolbar button pressed over `start`..`end` of `text` (a textarea's
 * `selectionStart` and `selectionEnd`): `{ value, start, end, refusal }`,
 * where `refusal` (`{ code, message }`) means nothing changed — past `max`.
 */
export function formatMessage(text, start, end, format, max) {
  const r = coreSync().composeFormat(
    str(text),
    Number.isInteger(start) ? start : undefined,
    Number.isInteger(end) ? end : undefined,
    String(format),
    max,
  );
  return { value: r.value, start: r.start, end: r.end, refusal: refusalOf(r.refusal) };
}

/** The longest template name, for its field's `maxLength`. */
export const messageTemplateLabelMax = () => coreSync().messageTemplateLabelMax();

/** Why Save New Template will not save, as `{ code, message }`, or null. */
export const messageTemplateRefusal = (label, body) => refusalOf(coreSync().messageTemplateRefusal(str(label), str(body)));

/**
 * What Save writes to `messageTemplates/{id}`, or null when it would be
 * refused. `serverTimestamp` is the SDK's sentinel for `updatedAt`.
 */
export function messageTemplateDocument({ label, subject, body } = {}, serverTimestamp = undefined) {
  const rows = coreSync().messageTemplateDocument(str(label), str(subject), str(body));
  return rows ? documentFromRows(rows, serverTimestamp) : null;
}

/**
 * Why the Custom Email Templates drawer's Save will not save (#608), as
 * `{ code, message }`, or null: no Title, or no Message.
 */
export const messageTemplateFormRefusal = (label, body) => refusalOf(coreSync().messageTemplateFormRefusal(str(label), str(body)));

/**
 * An edit in that drawer (#608): what is merged onto `messageTemplates/{id}`
 * — the fields that changed from `before` (the template as read) and
 * `updatedAt` — or null when the drawer's Save is refused.
 */
export function messageTemplateUpdateDocument(before = {}, { label, subject, body } = {}, serverTimestamp = undefined) {
  const rows = coreSync().messageTemplateUpdateDocument(
    str(before?.label), str(before?.subject), str(before?.body), str(label), str(subject), str(body),
  );
  return rows ? documentFromRows(rows, serverTimestamp) : null;
}

/**
 * The composer after a template is picked: `{ subject, body, refusal }` —
 * the message appended to, the subject the template's own whenever it has
 * one (MMS's pick, #605); with `refusal`, both as they were.
 */
export function applyMessageTemplate({ subject, body } = {}, template = {}) {
  const r = coreSync().applyMessageTemplate(str(subject), str(body), str(template?.subject), str(template?.body));
  return { subject: r.subject, body: r.body, refusal: refusalOf(r.refusal) };
}

// ── The placeholder menu (#604) ────────────────────────────────────────────
//
// MMS's twelve `%FirstName%`-style tokens: core/src/placeholders.rs, which
// says what of MMS's each keeps. The composer inserts them as text; the
// sender (functions/delivery/placeholders.js) fills them per recipient.

/** A word of the placeholder menu's, by code. */
export const placeholderWord = (code) => coreSync().placeholderMessage(String(code)) ?? String(code);

/** Every code `placeholderWord` answers. */
export const placeholderWordCodes = () => [...coreSync().placeholderMessageCodes()];

/** The menu, MMS's order: `{ key, token, label, source }`. */
export const composePlaceholders = () => [...coreSync().composePlaceholders()].map((p) => ({
  key: p.key, token: p.token, label: p.label, source: p.source,
}));

/**
 * A menu entry picked over `start`..`end` of `text`: `{ value, start, end,
 * refusal }` — its token in place of the selection, the cursor after it, or
 * with `refusal` nothing changed.
 */
export function insertPlaceholder(text, start, end, key, max) {
  const r = coreSync().composeInsertPlaceholder(
    str(text),
    Number.isInteger(start) ? start : undefined,
    Number.isInteger(end) ? end : undefined,
    String(key),
    max,
  );
  return { value: r.value, start: r.start, end: r.end, refusal: refusalOf(r.refusal) };
}

/** The Preview's reading: each token as `<< its menu words >>`. */
export const previewPlaceholders = (text) => coreSync().previewPlaceholders(str(text));

/** The menu's keys `text` uses, each once, in the menu's order. */
export const placeholdersUsed = (text) => [...coreSync().placeholdersUsed(str(text))];

const sides = (values) => {
  const entries = Object.entries(values || {});
  return [entries.map(([k]) => k), entries.map(([, v]) => str(v))];
};

/** `text` filled from `values` (`{ key: value }`), escaped for HTML when `html`. */
export function fillPlaceholders(text, values, html = false) {
  const [keys, vals] = sides(values);
  return coreSync().fillPlaceholders(str(text), keys, vals, Boolean(html));
}

/** The rendered `part` ('html' or 'text') with each token as its marker. */
export const markPlaceholders = (text, part) => coreSync().markPlaceholders(str(text), String(part));

/** One recipient's `{ marker: value }`, every marker of both parts. */
export function placeholderSubstitutions(values) {
  const [keys, vals] = sides(values);
  return Object.fromEntries([...coreSync().placeholderSubstitutions(keys, vals)].map((s) => [s.marker, s.value]));
}

/** `%Balance%`: the Region's amount (no sign) and Owing or Credit by `minor`'s sign. */
export const placeholderBalance = (formatted, minor) => coreSync().placeholderBalance(str(formatted), Number(minor) || 0);

/** A display name's first word and the rest: `[first, last]`. */
export const splitDisplayName = (display) => [...coreSync().placeholderSplitName(str(display))];

// ── Attach Files (#605) ────────────────────────────────────────────────────
//
// Real attachments, as MMS sends them: a pick is uploaded to Storage at once
// and the message names each file as `{cap}/{name}` in its context. The
// bounds, the name a file goes under and the reading of an entry are
// core/src/compose.rs's, which carries MMS's reading and the argument for
// each number. The web composer (src/hooks/useNewMessage.js) and the sender
// (functions/delivery/attachments.js) both come through here.

/**
 * The bounds: `{ maxBytes, totalMaxBytes, max, nameMax, keepDays }` — the
 * most one file and one message's files may be, in bytes; the most files;
 * the longest name; the days the bytes are kept past their last use.
 */
export function attachmentPolicy() {
  const p = coreSync().composeAttachmentPolicy();
  return {
    maxBytes: p.maxBytes, totalMaxBytes: p.totalMaxBytes, max: p.max, nameMax: p.nameMax, keepDays: p.keepDays,
  };
}

/** A picked file's name as the family's mail client shows it. */
export const attachmentName = (raw) => coreSync().composeAttachmentName(str(raw));

/**
 * What a photo or video from a phone's library is called when its picker gave
 * only a media id or no name (#611): `Video 2026-10-03.mp4`. The phones' — a
 * browser's file input always has the file's own name — and here so the
 * vectors reach it through the same door as every other adapter.
 */
export const attachmentMediaName = (name, contentType, taken = '', picked = '') =>
  coreSync().composeAttachmentMediaName(str(name), str(contentType), str(taken), str(picked));

/** How a message names an uploaded file, `{cap}/{name}`, or null. */
export const attachmentEntry = (cap, name) => coreSync().composeAttachmentEntry(str(cap), str(name)) ?? null;

/** A message's entries read back: `[{ cap, name }]`, each file once, in order. */
export const messageAttachments = (entries) => [
  ...coreSync().composeAttachments((Array.isArray(entries) ? entries : []).filter((e) => typeof e === 'string')),
].map((a) => ({ cap: a.cap, name: a.name }));

const sizes = (list) => (Array.isArray(list) ? list : []).map((n) => Number(n) || 0);

/**
 * Why a picked file will not be attached, as `{ code, message }`, or null.
 * `attached` is the sizes, in bytes, of the files already on the message.
 */
export const attachmentRefusal = (name, bytes, attached = []) => refusalOf(
  coreSync().composeAttachmentRefusal(str(name), Number(bytes), sizes(attached)),
);

/** Whether files of these sizes are more than one message may carry. */
export const attachmentsOver = (list) => coreSync().composeAttachmentsOver(sizes(list));

/** The sender's sentence for a file that is no longer kept. */
export const attachmentMissingText = (name) => coreSync().composeAttachmentMissingText(str(name));
